import { ASSET_OWN_REGISTER, type SearchFn, type SuggestionRow } from '@bms2/shared';
import { apiFetch } from '../records/api';

/**
 * The asset register's API paths (piece 28). 🔨 Each is relative to the API's
 * own root and never repeats `/api`; every one is declared in `apiPaths.test.ts`.
 */
export const ASSETS_PATH = '/assets';
export const ASSET_TRANSFER_PATH = `${ASSETS_PATH}/transfer`;

/** 🔒 Decision 12540: the Assets page's own register, the organization's trees alone. */
export const ASSETS_OWN_PATH = `${ASSETS_PATH}?own=${ASSET_OWN_REGISTER}`;

/** 🔒 Decision 12540: the trees standing at one site, which its Site page's Assets tile draws. */
export function siteAssetsPath(siteId: string): string {
  return `${ASSETS_PATH}?siteId=${encodeURIComponent(siteId)}`;
}

export function assetPath(id: string): string {
  return `${ASSETS_PATH}/${encodeURIComponent(id)}`;
}

/** 🔒 The plant board's arrows (decision 12561): a POST makes one between two siblings, a DELETE of one removes it. */
export const ASSET_FLOW_LINKS_PATH = `${ASSETS_PATH}/flow-links`;

export function assetFlowLinkPath(linkId: string): string {
  return `${ASSET_FLOW_LINKS_PATH}/${encodeURIComponent(linkId)}`;
}

/** 🔒 A Function's parts in working order (decision 12833): POST names every part once, first to last. */
export function assetPartOrderPath(id: string): string {
  return `${assetPath(id)}/part-order`;
}

/** 🔒 A Function's ports (decision 12832): POST makes one, In or Out with an optional name. */
export function assetPortsPath(id: string): string {
  return `${assetPath(id)}/ports`;
}

/** 🔒 One port, which DELETE removes with its arrows (decision 12832). */
export function assetPortPath(portId: string): string {
  return `${ASSETS_PATH}/ports/${encodeURIComponent(portId)}`;
}

/** 🔒 A card's kept place on the plant board (decision 12561), written without moving the node's version. */
export function assetPlacePath(id: string): string {
  return `${assetPath(id)}/place`;
}

/** The node's one picture (decision 1291): a multipart POST sets it and a DELETE clears it. */
export function assetImagePath(id: string): string {
  return `${assetPath(id)}/image`;
}

export function assetLookupPath(kind: 'company' | 'employment' | 'site' | 'item' | 'node' | 'template' | 'meter' | 'account' | 'instrument'): string {
  return `${ASSETS_PATH}/lookups/${kind}`;
}

export function ownerLinkClosePath(linkId: string): string {
  return `${ASSETS_PATH}/owner-links/${encodeURIComponent(linkId)}/close`;
}

export function custodyReturnPath(linkId: string): string {
  return `${ASSETS_PATH}/custody/${encodeURIComponent(linkId)}/return`;
}

export function componentAttributePath(assetId: string, attributeId?: string): string {
  return attributeId ? `${assetPath(assetId)}/attributes/${encodeURIComponent(attributeId)}` : `${assetPath(assetId)}/attributes`;
}

export function schedulePath(scheduleId: string): string {
  return `${ASSETS_PATH}/schedules/${encodeURIComponent(scheduleId)}`;
}

/** 🔒 The visit's Load-Out Check (piece 28; decision 0151), asked on the Work Order page at dispatch (T2367). */
export function loadOutPath(workOrderId: string): string {
  return `${ASSETS_PATH}/load-outs/${encodeURIComponent(workOrderId)}`;
}

export function loadOutAcknowledgementsPath(workOrderId: string): string {
  return `${loadOutPath(workOrderId)}/acknowledgements`;
}

/** 🔒 The attributes a Work Order line naming an asset is offered: Visibility at Work Orders alone (piece 28; T2367). */
export function workOrderLineAttributesPath(lineId: string): string {
  return `${ASSETS_PATH}/work-order-lines/${encodeURIComponent(lineId)}/attributes`;
}

/** 🔒 One schedule's month posted as one Manual Journal, once (decision 9141; T2367). */
export function depreciationPostPath(assetId: string, scheduleId: string): string {
  return `${assetPath(assetId)}/depreciation/${encodeURIComponent(scheduleId)}/post`;
}

export function conversionPath(dealId: string): string {
  return `${ASSETS_PATH}/conversions/${encodeURIComponent(dealId)}`;
}

/** 🔒 The asset template library's own routes (decisions 1292 and 1293), beside the register's and never under `/assets/:id`. */
export const ASSET_TEMPLATES_PATH = '/asset-templates';

export function assetTemplatePath(id: string): string {
  return `${ASSET_TEMPLATES_PATH}/${encodeURIComponent(id)}`;
}

export function assetTemplateRetirePath(id: string): string {
  return `${assetTemplatePath(id)}/retire`;
}

export function assetTemplateBuildPath(id: string): string {
  return `${assetTemplatePath(id)}/build`;
}

/** A picker's search over one of the register's lookups, in the shared Dropdown's shape. */
export function assetSearch(kind: Parameters<typeof assetLookupPath>[0], apiBaseUrl = ''): SearchFn {
  return async (query, signal) => {
    const response = await apiFetch(`${apiBaseUrl}${assetLookupPath(kind)}?q=${encodeURIComponent(query)}`, { signal }).catch(/* silent: a fetch the server never answered cannot reach the server's log either; the page says it could not reach the server. */ () => null);
    if (!response?.ok) return [];
    return ((await response.json()) as SuggestionRow[]) ?? [];
  };
}
